import { createContext, useContext, useState, useCallback, useMemo, useRef, ReactNode } from 'react';

export interface BlockSelection {
  kind?: string;
  path?: number[];
  elementId?: string;
  [key: string]: unknown;
}

export interface TableSelection {
  cells?: unknown[];
  element?: { uuid?: string };
  referenceDataSuperId?: string;
  onTagsCreated?: () => void;
  [key: string]: unknown;
}

export interface DraftTagRef {
  onId?: string;
  [key: string]: unknown;
}

export interface ContinuationTagRef {
  tagId?: string;
  [key: string]: unknown;
}

export interface TagSelectionContextValue {
  tableSelection: TableSelection | null;
  blockSelection: BlockSelection | null;
  blockScope: string;
  clickedTagIds: string[] | null;
  clickedFromContent: boolean;
  draftTag: DraftTagRef | null;
  continuationTag: ContinuationTagRef | null;
  setTableTagSelection: (selection: TableSelection | null) => void;
  clearTableTagSelection: () => void;
  setBlockSelection: (selection: BlockSelection | null) => void;
  clearBlockSelection: () => void;
  setBlockScope: (value: string) => void;
  setClickedTagIds: (value: string[] | null, options?: { fromContent?: boolean }) => void;
  setDraftTag: (value: DraftTagRef | null) => void;
  setContinuationTag: (value: ContinuationTagRef | null) => void;
}

export const TagSelectionContext = createContext<TagSelectionContextValue>({} as TagSelectionContextValue);

export const TagSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [tableSelection, setTableSelection] = useState<TableSelection | null>(null);
  const [blockSelection, setBlockSelectionState] = useState<BlockSelection | null>(null);
  const [blockScope, setBlockScope] = useState<string>('cell');
  const [clickedTagIds, setClickedTagIdsState] = useState<string[] | null>(null);
  const [clickedFromContent, setClickedFromContent] = useState(false);
  const [draftTag, setDraftTag] = useState<DraftTagRef | null>(null);
  const [continuationTag, setContinuationTag] = useState<ContinuationTagRef | null>(null);

  const setTableTagSelection = useCallback((selection: TableSelection | null) => {
    setTableSelection(selection);
    setBlockSelectionState(null);
  }, []);

  const clearTableTagSelection = useCallback(() => {
    setTableSelection(null);
  }, []);

  const blockSelectionIdRef = useRef<string | undefined>(undefined);
  const setBlockSelection = useCallback((selection: BlockSelection | null) => {
    const sameElement = !!selection?.elementId && selection.elementId === blockSelectionIdRef.current;
    blockSelectionIdRef.current = selection?.elementId;
    setBlockSelectionState(selection);
    setTableSelection(null);
    if (!sameElement) setBlockScope(selection?.kind === 'list' ? 'block' : selection?.kind === 'paragraph' ? 'text' : 'cell');
  }, []);

  const clearBlockSelection = useCallback(() => {
    blockSelectionIdRef.current = undefined;
    setBlockSelectionState(null);
    setBlockScope('cell');
  }, []);

  const setClickedTagIds = useCallback((value: string[] | null, options?: { fromContent?: boolean }) => {
    setClickedTagIdsState(value);
    setClickedFromContent(!!options?.fromContent);
  }, []);

  const value = useMemo<TagSelectionContextValue>(
    () => ({
      tableSelection,
      blockSelection,
      blockScope,
      clickedTagIds,
      clickedFromContent,
      draftTag,
      continuationTag,
      setTableTagSelection,
      clearTableTagSelection,
      setBlockSelection,
      clearBlockSelection,
      setBlockScope,
      setClickedTagIds,
      setDraftTag,
      setContinuationTag,
    }),
    [
      tableSelection,
      blockSelection,
      blockScope,
      clickedTagIds,
      clickedFromContent,
      draftTag,
      continuationTag,
      setTableTagSelection,
      clearTableTagSelection,
      setBlockSelection,
      clearBlockSelection,
      setClickedTagIds,
    ]
  );

  return <TagSelectionContext.Provider value={value}>{children}</TagSelectionContext.Provider>;
};

export const useTagSelection = () => useContext(TagSelectionContext);
